"use client";

import React from "react";
import { motion } from "framer-motion";
import { IoRefresh } from "react-icons/io5";
import {
  staggerContainer,
  staggerItem,
  fadeInUp,
  textReveal,
} from "../lib/animations";

interface ModalLink {
  text: string;
  url: string;
}

interface ModalBullet {
  text: string;
  links?: ModalLink[];
}

interface ModalParagraph {
  text?: string;
  isBold?: boolean;
  bullets?: ModalBullet[];
  links?: ModalLink[];
  buttons?: ModalButton[];
}

interface ModalButton {
  text: string;
  action?: () => void;
  variant?: "primary" | "secondary";
}

interface ModalSection {
  paragraphs: ModalParagraph[];
  images?: string[];
  layout?: "text-only" | "text-image" | "image-text" | "text-top" | "image-top";
  title?: string;
}

const renderLinks = (links: ModalLink[]) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {links.map((link, idx) => (
      <a
        key={idx}
        href={link.url}
        className="text-blue-600 hover:text-blue-800 underline lg:text-xl text-md"
        target="_blank"
        rel="noopener noreferrer"
      >
        {link.text}
      </a>
    ))}
  </div>
);

const renderBullets = (bullets: ModalBullet[]) => (
  <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
    {bullets.map((bullet, idx) => (
      <li key={idx} className="text-gray-600 lg:text-xl text-md">
        {bullet.text}
        {bullet.links && renderLinks(bullet.links)}
      </li>
    ))}
  </ul>
);

const renderParagraph = (paragraph: ModalParagraph, idx: number) => (
  <motion.div
    key={idx}
    className="mb-4"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: idx * 0.1 }}
  >
    <p
      className={`text-gray-600 lg:text-xl text-xl leading-relaxed ${
        paragraph.isBold ? "font-semibold" : ""
      }`}
    >
      {paragraph.text}
      {paragraph.links && renderLinks(paragraph.links)}
    </p>
    {paragraph.bullets && renderBullets(paragraph.bullets)}
  </motion.div>
);

const renderSection = (section: ModalSection, sectionIdx: number) => (
  <div key={sectionIdx} className="mb-8">
    <div
      className={`${
        section.layout === "text-image"
          ? "grid grid-cols-1 lg:grid-cols-1 lg:gap-6 gap-2"
          : section.layout === "image-text"
          ? "grid grid-cols-1 lg:grid-cols-2 lg:gap-6 gap-2"
          : section.layout === "text-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : section.layout === "image-top"
          ? "flex flex-col lg:gap-6 gap-2"
          : "block"
      }`}
    >
      {/* Text Content */}
      <div
        className={`w-full bg-[#f5f5f7] rounded-3xl ${
          section?.layout === "text-only" || !section?.layout
            ? " p-5"
            : "pb-0 lg:pt-10 pt-5 p-10"
        }`}
      >
        {section?.title && (
          <motion.h3
            className="lg:text-3xl text-lg font-bold text-gray-800 mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {section?.title}
          </motion.h3>
        )}
        <div
          className={`${
            section?.images && section?.images?.length === 0 ? "pb-0" : ""
          } ${
            section.layout === "image-text"
              ? "order-2"
              : section.layout === "image-top"
              ? "order-2"
              : "lg:mb-10 mb-3 "
          }`}
        >
          {section.paragraphs.map((paragraph, idx) =>
            renderParagraph(paragraph, idx)
          )}
        </div>
      </div>
    </div>
  </div>
);

const refundData: {
  category: string;
  title: string;
  subtitle: string;
  sections: ModalSection[];
} = {
  category: "Legal & Compliance",
  title: "Refund Policy",
  subtitle: "Our refund and cancellation terms for all services",
  sections: [
    {
      title: "1. General Refund Policy",
      paragraphs: [
        {
          text: "We strive to provide exceptional service and ensure complete customer satisfaction. However, we understand that circumstances may arise where a refund is necessary. Our refund policy is designed to be fair to both our clients and our business.",
        },
      ],
      layout: "text-only",
    },
    {
      title: "2. Custom Development Projects",
      paragraphs: [
        {
          text: "Before Project Commencement:",
          bullets: [
            {
              text: "100% refund if cancellation occurs before project work begins",
            },
            { text: "Refund processed within 5-7 business days" },
            {
              text: "Any consultation fees may be non-refundable if substantial work was performed",
            },
          ],
        },
        {
          text: "During Development Phase:",
          bullets: [
            {
              text: "Partial refund based on work completed and milestones achieved",
            },
            {
              text: "Refund calculation: Total payment minus work completed value",
            },
            { text: "Client receives all completed work and source code" },
            { text: "Refund processed within 10-14 business days" },
          ],
        },
        {
          text: "After Project Completion:",
          bullets: [
            { text: "No refunds after project completion and delivery" },
            {
              text: "Bug fixes and minor adjustments included in warranty period",
            },
            { text: "Major changes may require additional payment" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "3. Design Services (UI/UX, Logo, Branding)",
      paragraphs: [
        {
          text: "Before Design Work Begins:",
          bullets: [
            {
              text: "100% refund if cancellation occurs before design work starts",
            },
            { text: "Refund processed within 5-7 business days" },
          ],
        },
        {
          text: "During Design Process:",
          bullets: [
            { text: "Partial refund based on design iterations completed" },
            { text: "Client receives all completed design files" },
            { text: "Refund processed within 7-10 business days" },
          ],
        },
        {
          text: "After Design Approval:",
          bullets: [
            { text: "No refunds after final design approval" },
            { text: "Minor revisions included in service" },
            { text: "Major redesigns may require additional payment" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "4. Consulting and Support Services",
      paragraphs: [
        {
          text: "Hourly Consulting:",
          bullets: [
            { text: "No refunds for completed consulting hours" },
            { text: "Unused hours may be refunded or credited for future use" },
            {
              text: "Cancellation with 24-hour notice required for unused hours",
            },
          ],
        },
        {
          text: "Support Packages:",
          bullets: [
            { text: "Pro-rated refunds for unused support time" },
            { text: "Refund calculation based on remaining support hours" },
            { text: "Processing time: 7-10 business days" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "5. Non-Refundable Services",
      paragraphs: [
        {
          text: "The following services are non-refundable:",
          bullets: [
            { text: "Domain registration and hosting fees" },
            { text: "Third-party software licenses and tools" },
            { text: "Completed work that has been delivered and approved" },
            { text: "Rush fees and expedited service charges" },
            { text: "Consultation fees for completed sessions" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "6. Refund Process",
      paragraphs: [
        {
          text: "How to Request a Refund:",
          bullets: [
            { text: "Submit refund request via email to hello@zysoftec.com" },
            { text: "Include project details and reason for refund" },
            { text: "Provide payment transaction details" },
            { text: "Allow 2-3 business days for review" },
          ],
        },
        {
          text: "Refund Processing:",
          bullets: [
            { text: "Refunds are processed to the original payment method" },
            { text: "Processing time varies by payment provider" },
            { text: "You will receive confirmation email once processed" },
            { text: "Bank transfers may take 3-5 additional business days" },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "7. Dispute Resolution",
      paragraphs: [
        {
          text: "If you are dissatisfied with our services or refund decision:",
          bullets: [
            { text: "Contact us directly to discuss the issue" },
            {
              text: "We will review your case and provide a response within 5 business days",
            },
            {
              text: "We may offer alternative solutions such as additional work or credits",
            },
            {
              text: "If disputes cannot be resolved, we may engage in mediation",
            },
          ],
        },
      ],
      layout: "text-only",
    },
    {
      title: "8. Quality Assurance",
      paragraphs: [
        {
          text: "To ensure quality and reduce the need for refunds:",
          bullets: [
            { text: "We provide detailed project proposals and timelines" },
            { text: "Regular progress updates and milestone reviews" },
            { text: "Client feedback and approval at each stage" },
            { text: "Comprehensive testing and quality checks" },
            { text: "Post-launch support and maintenance options" },
          ],
        },
      ],
      layout: "text-only",
    },
    // {
    //   title: "9. Exceptions and Special Circumstances",
    //   paragraphs: [
    //     {
    //       text: "We may consider exceptions to our refund policy in special circumstances:",
    //       bullets: [
    //         { text: "Technical issues beyond our control" },
    //         { text: "Significant delays caused by our team" },
    //         { text: "Failure to meet agreed-upon specifications" },
    //         { text: "Long-term client relationships" },
    //         { text: "Force majeure events" },
    //       ],
    //     },
    //   ],
    //   layout: "text-only",
    // },
    {
      title: "9. Contact Information",
      paragraphs: [
        {
          text: "For refund requests or questions about this policy:",
        },
        {
          text: "Email: hello@zysoftec.com",
          isBold: true,
        },
        {
          text: "Phone: +92 3175682252",
          isBold: true,
        },
        {
          text: "Address: ZySoftec, Pakistan",
          isBold: true,
        },
        {
          text: "Please include 'Refund Request' in the subject line for faster processing.",
        },
      ],
      layout: "text-only",
    },
  ],
};

export default function RefundPolicy() {
  return (
    <motion.div
      className="min-h-screen bg-white"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Header */}
      <motion.div
        className="lg:pt-20 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {refundData.category && (
          <motion.p
            className="lg:text-base text-sm text-gray-500 font-medium mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {refundData.category}
          </motion.p>
        )}
        <motion.h2
          className="lg:text-4xl text-2xl font-bold text-gray-900 leading-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {refundData.title}
        </motion.h2>

        {refundData.subtitle && (
          <motion.p
            className="lg:text-lg text-sm text-gray-600 mt-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            {refundData.subtitle}
          </motion.p>
        )}
      </motion.div>

      {/* Main Content */}
      <div className="lg:pt-10 pt-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-5">
        {refundData.sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 + idx * 0.1 }}
          >
            {renderSection(section, idx)}
          </motion.div>
        ))}
      </div>

      {/* Last Updated */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 1 }}
      >
        <div className="bg-gray-50 rounded-lg p-4 text-center max-w-4xl mx-auto">
          <p className="text-sm text-gray-600">
            <strong>Last Updated:</strong> December 2024
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
